// Font caching utility to download and store custom fonts locally

const FONT_CACHE_KEY = 'audome_custom_fonts_cache';
const FONT_CACHE_VERSION = 'v1';

interface CachedFont {
  id: string;
  fontName: string;
  fontFamily: string;
  base64Data: string;
  url: string;
  cachedAt: number;
}

interface FontCacheData {
  version: string;
  fonts: CachedFont[];
  lastUpdate: number;
}

/**
 * Get cached fonts from localStorage
 */
export const getCachedFonts = (): CachedFont[] => {
  try {
    const cached = localStorage.getItem(FONT_CACHE_KEY);
    if (!cached) return [];
    
    const data: FontCacheData = JSON.parse(cached);
    
    // Check version
    if (data.version !== FONT_CACHE_VERSION) {
      localStorage.removeItem(FONT_CACHE_KEY);
      return [];
    }
    
    return data.fonts || [];
  } catch (err) {
    console.error('Error reading font cache:', err);
    return [];
  }
};

/**
 * Save fonts to localStorage cache
 */
export const saveFontsToCache = (fonts: CachedFont[]): void => {
  try {
    const data: FontCacheData = {
      version: FONT_CACHE_VERSION,
      fonts,
      lastUpdate: Date.now()
    };
    
    localStorage.setItem(FONT_CACHE_KEY, JSON.stringify(data));
    // Fonts cached successfully
  } catch (err) {
    console.error('Error saving fonts to cache:', err);
  }
};

/**
 * Download font file and convert to base64
 */
const downloadFontAsBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]); // Get base64 part only
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Load user's custom fonts and cache them
 */
export const loadAndCacheUserFonts = async (
  userId: string,
  customFonts: Array<{ id: string; font_name: string; font_file_url: string; font_family_css: string }>
): Promise<CachedFont[]> => {
  // Loading and caching user fonts
  
  // Get current cache
  const cachedFonts = getCachedFonts();
  const cachedFontIds = new Set(cachedFonts.map(f => f.id));
  
  // Find fonts that need to be downloaded
  const fontsToDownload = customFonts.filter(f => !cachedFontIds.has(f.id));
  
  if (fontsToDownload.length === 0) {
    return cachedFonts;
  }
  
  // Downloading new fonts
  
  // Download new fonts
  const newCachedFonts: CachedFont[] = [];
  
  for (const font of fontsToDownload) {
    try {
      const base64Data = await downloadFontAsBase64(font.font_file_url);
      const familyName = font.font_family_css.replace(/['"]/g, '').split(',')[0].trim();
      
      newCachedFonts.push({
        id: font.id,
        fontName: font.font_name,
        fontFamily: familyName,
        base64Data,
        url: font.font_file_url,
        cachedAt: Date.now()
      });
      
      // Cached font
    } catch (err) {
      console.error(`❌ Failed to cache font ${font.font_name}:`, err);
    }
  }
  
  // Merge with existing cache
  const allCachedFonts = [...cachedFonts, ...newCachedFonts];
  
  // Remove fonts that no longer exist in user's custom fonts
  const currentFontIds = new Set(customFonts.map(f => f.id));
  const filteredFonts = allCachedFonts.filter(f => currentFontIds.has(f.id));
  
  // Save to cache
  saveFontsToCache(filteredFonts);
  
  return filteredFonts;
};

/**
 * Inject cached fonts into document as @font-face rules
 */
export const injectCachedFonts = (cachedFonts: CachedFont[]): void => {
  // Injecting cached fonts into document
  
  // Check if style element already exists
  let styleEl = document.getElementById('audome-cached-fonts') as HTMLStyleElement;
  
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'audome-cached-fonts';
    document.head.appendChild(styleEl);
  }
  
  // Build @font-face rules
  const fontFaceRules = cachedFonts.map(font => `
    @font-face {
      font-family: '${font.fontFamily}';
      src: url(data:font/truetype;charset=utf-8;base64,${font.base64Data}) format('truetype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `).join('\n');
  
  styleEl.textContent = fontFaceRules;
  
  // Injected fonts into document
};

/**
 * Clear font cache
 */
export const clearFontCache = (): void => {
  localStorage.removeItem(FONT_CACHE_KEY);
  
  // Remove injected fonts from document
  const styleEl = document.getElementById('audome-cached-fonts');
  if (styleEl) {
    styleEl.remove();
  }
  
  // Font cache cleared
};

